import React, {
  createContext, useState, useContext, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import config from '../config';

const STORAGE_KEY = 'hippo-dashboard-preferences';

const DEFAULT_PREFERENCES = {
  environment: 'NBL',
  selectedCards: ['metRate', 'heartRate'],
  cardOrder: ['metRate', 'heartRate'],
};

const CARD_AVAILABILITY = {
  CHAPEA: ['metRate', 'heartRate'],
  NBL: ['metRate', 'heartRate', 'ecsSuit'],
  ARGOS: ['metRate', 'heartRate', 'thermal'],
  'FIELD TEST': ['metRate', 'heartRate'],
};

function loadPreferences(configMode, allowedEnvironments) {
  const fullEnvOptions = configMode === 'CHAPEA' ? ['CHAPEA'] : ['NBL', 'ARGOS', 'FIELD TEST'];
  const envOptions = allowedEnvironments
    ? fullEnvOptions.filter((env) => allowedEnvironments.includes(env))
    : fullEnvOptions;
  const defaultEnv = envOptions[0] || 'NBL';
  const defaultCards = configMode === 'CHAPEA' ? ['metRate', 'heartRate'] : ['metRate', 'heartRate'];
  const base = {
    ...DEFAULT_PREFERENCES,
    environment: defaultEnv,
    selectedCards: defaultCards,
    cardOrder: defaultCards,
  };
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      const env = envOptions.includes(parsed.environment) ? parsed.environment : defaultEnv;
      const available = CARD_AVAILABILITY[env] || defaultCards;
      const selectedCards = (parsed.selectedCards || []).filter((c) => available.includes(c));
      const cardOrder = (parsed.cardOrder || []).filter((c) => available.includes(c));
      return {
        ...base,
        ...parsed,
        environment: env,
        selectedCards: selectedCards.length ? selectedCards : defaultCards,
        cardOrder: cardOrder.length ? cardOrder : defaultCards,
      };
    }
  } catch {
    // ignore
  }
  return base;
}

function savePreferences(prefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // ignore
  }
}

const DashboardPreferencesContext = createContext();

export function DashboardPreferencesProvider({ children, allowedEnvironments }) {
  const configMode = config?.mode || 'FULL';
  const [preferences, setPreferencesState] = useState(() => loadPreferences(configMode, allowedEnvironments));

  const setEnvironment = useCallback((environment) => {
    setPreferencesState((prev) => {
      const available = CARD_AVAILABILITY[environment] || [];
      let selectedCards = prev.selectedCards.filter((c) => available.includes(c));
      if (selectedCards.length === 0 && available.length > 0) {
        selectedCards = [available[0]];
      }
      const cardOrder = [...new Set([...prev.cardOrder.filter((c) => available.includes(c)), ...available])];
      const next = {
        ...prev,
        environment,
        selectedCards,
        cardOrder,
      };
      savePreferences(next);
      return next;
    });
  }, []);

  const setSelectedCards = useCallback((selectedCards) => {
    setPreferencesState((prev) => {
      const next = { ...prev, selectedCards };
      savePreferences(next);
      return next;
    });
  }, []);

  const setCardOrder = useCallback((cardOrder) => {
    setPreferencesState((prev) => {
      const next = { ...prev, cardOrder };
      savePreferences(next);
      return next;
    });
  }, []);

  const toggleCard = useCallback((cardId) => {
    setPreferencesState((prev) => {
      const available = CARD_AVAILABILITY[prev.environment] || [];
      if (!available.includes(cardId)) return prev;
      const selected = prev.selectedCards.includes(cardId)
        ? prev.selectedCards.filter((c) => c !== cardId)
        : [...prev.selectedCards, cardId];
      if (selected.length === 0) return prev;
      const cardOrder = prev.cardOrder.includes(cardId)
        ? prev.cardOrder
        : [...prev.cardOrder, cardId];
      const next = { ...prev, selectedCards: selected, cardOrder };
      savePreferences(next);
      return next;
    });
  }, []);

  const availableCards = CARD_AVAILABILITY[preferences.environment] || [];

  const value = {
    ...preferences,
    availableCards,
    setEnvironment,
    setSelectedCards,
    setCardOrder,
    toggleCard,
  };

  return (
    <DashboardPreferencesContext.Provider value={value}>
      {children}
    </DashboardPreferencesContext.Provider>
  );
}

DashboardPreferencesProvider.propTypes = {
  children: PropTypes.node.isRequired,
  allowedEnvironments: PropTypes.arrayOf(PropTypes.string),
};

DashboardPreferencesProvider.defaultProps = {
  allowedEnvironments: null,
};

export function useDashboardPreferences() {
  const context = useContext(DashboardPreferencesContext);
  if (!context) {
    throw new Error('useDashboardPreferences must be used within DashboardPreferencesProvider');
  }
  return context;
}

export { CARD_AVAILABILITY };
